"use client";

import { useBoundStore } from "@/lib/stores/useBoundStore";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import styled from "styled-components";

export const PageWrapper = styled.div<{
  $flex: boolean;
  $requireScroll: boolean;
  $isAdminDashboard?: boolean;
  $isLegal?: boolean;
}>`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: ${(props) => (props.$requireScroll ? "auto" : "hidden")};

  main {
    flex: 1;
    display: ${(props) => props.$flex && "flex"};
    min-height: 0;
    overflow: ${(props) => (props.$requireScroll ? "auto" : "hidden")};
  }

  @media (max-width: 1024px) {
    overflow: auto;
    min-height: 100vh;
    height: unset;

    main {
      padding-top: 50.5px;
      flex: 1 0 auto;
      overflow: auto;
      height: unset;
      min-height: 100vh;
    }

    ${(props) =>
      props.$isLegal &&
      `
      // min-page wrapper for legal-specific route
      main {
        padding-top: 0px;
        min-height: auto;
        flex: 1 0 auto;
        overflow: visible;
      }
      `}

    ${(props) =>
      props.$isAdminDashboard &&
      `
      overflow: auto;
      min-height: 100vh;
      height: revert;

      main {
        padding-top: 0px;
        flex: 1 0 auto;
        overflow: auto;
        height: revert;
        min-height: 100vh;
      }
      `}
  }
`;

export default function MinPageWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const isOverlay = useBoundStore((state) => state.overlayType);
  const isModalOpen = useBoundStore((state) => state.isModalOpen);
  const isQOpen = useBoundStore((state) => state.isQOpen);
  const adminModalOpen = useBoundStore((state) => state.adminModalOpen);
  const isAIOpen = useBoundStore((state) => state.isAIOpen);
  const isCreateAction = useBoundStore((state) => state.isCreateAction);
  const isCreate = useBoundStore((state) => state.isCreate);
  const isDelete = useBoundStore((state) => state.isDelete);

  useEffect(() => {
    const anyModalOpen =
      isOverlay !== "" ||
      isModalOpen ||
      isQOpen ||
      adminModalOpen ||
      isAIOpen ||
      isCreateAction ||
      isCreate ||
      isDelete;

    if (anyModalOpen) {
      document.body.classList.add("no-scroll");
      document.documentElement.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
      document.documentElement.classList.remove("no-scroll");
    }
  }, [isOverlay, isModalOpen, isQOpen, adminModalOpen, isAIOpen, isCreateAction, isCreate, isDelete]);

  return (
    <PageWrapper
      $requireScroll={pathname.includes("patient")}
      $isLegal={pathname.includes("legal")}
      $flex={
        pathname.includes("learning") ||
        pathname.includes("workflow") ||
        pathname.includes("metrics") ||
        pathname.includes("technique") ||
        pathname.includes("outcomes") ||
        pathname.includes("practice-management") ||
        pathname.includes("predefined-action-items") ||
        pathname.includes("care-plan") ||
        pathname.includes("cases") ||
        (pathname.includes("patientpath") &&
          !pathname.match(/patientpath\/[^/]/)) ||
        (pathname.includes("patientpath") && pathname.includes("/info"))
      }
      $isAdminDashboard={pathname.includes("admin")}
    >
      {children}
    </PageWrapper>
  );
}
